/* eslint-disable react/no-danger */
import React from 'react';
import Image from 'next/future/image';
import Head from 'next/head';
import Link from 'next/link';
import { GetStaticProps } from 'next';
import { Blog } from '@lambda/commons/apis/shopify/types/storefront';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useRouter } from 'next/router';
import Sidebar from '@/components/blogs/sidebar';
import shopify from '@/lib/shopify';
import * as Icons from '@/components/homepage/Icons';
import styles from '../../components/blogs/blog.module.scss';
import { logger } from '@/lib/logger';

type Props = { blog: Blog };

export const getStaticPaths = async () => {
  const response = await shopify.fetch({
    query: `query ($query: String!) {
              blog(handle: $query){
                title
                articles(first:40 reverse:true){
                  edges{
                    node{
                      title
                      handle
                      content
                      contentHtml
                      excerpt
                      tags 
                      id
                      publishedAt
                      image { url }
                      onlineStoreUrl
                    }
                  }
                }
              }
            }`,
    variables: { query: 'News' },
    skipLogging: true,
  });
  const { blog } = response;

  if (blog == null) {
    logger.info('Blog not found');

    return { notFound: true };
  }
  const paths = blog.articles.edges.map((a) => ({
    params: { handle: a.node.handle },
  }));

  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps<Props> = async () => {
  const response = await shopify.fetch({
    query: `query ($query: String!) {
              blog(handle: $query){
                title
                articles(first:40 reverse:true){
                  edges{
                    node{
                      title
                      handle
                      content
                      contentHtml
                      excerpt
                      tags 
                      id
                      publishedAt
                      image { url }
                      onlineStoreUrl
                    }
                  }
                }
              }
            }`,
    variables: { query: 'News' },
    skipLogging: true,
  });
  const { blog } = response;

  if (blog == null) return { notFound: true };

  const props = {
    blog,
  };

  return { props, revalidate: 10 };
};

export default function Blogs(props: Props) {
  const router = useRouter();

  dayjs.extend(advancedFormat);
  const { articles } = props.blog;
  const { handle } = router.query;
  const article = articles.edges.find((e) => e.node.handle === handle);
  const articleIndex = articles.edges.findIndex(
    (e) => e.node.handle === handle,
  );
  const prevArticle = articles.edges[articleIndex - 1];
  const nextArticle = articles.edges[articleIndex + 1];
  const contentHtml = article?.node.contentHtml.replace(`/pages/faq`, '/help');

  return (
    <div>
      <Head>
        <title>{article?.node.title} </title>
      </Head>
      <div className="reebelo-container pt-5 pb-6 md:pt-8 md:pb-20">
        <h1 className="mb-4 text-left text-base font-semibold !leading-5 text-gray-700 xxs:text-lg xxs:!leading-6 xs:text-xl sm:!leading-normal md:text-2xl xl:text-3xl xl:!leading-9">
          {article?.node.title}
        </h1>
        <p className="mb-5 text-sm text-gray-700 md:mb-7 md:text-base">
          {article && article.node.tags.length > 0 && (
            <>
              <span>{article?.node.tags[0]} </span>
              <span className="mx-2.5 inline-block h-1.5 w-1.5 rounded-full bg-gray-700">
                {' '}
              </span>
            </>
          )}

          <span>
            {' '}
            {dayjs(article?.node.publishedAt).format('MMM Do YYYY')}{' '}
          </span>
        </p>
        <div className="flex flex-wrap gap-5 md:gap-8 lg:flex-nowrap">
          <div className="w-full lg:w-[60%] xl:w-[70%]">
            {article && article.node.image?.url && (
              <div className="w-full">
                <div className="flex w-full overflow-hidden">
                  <Image
                    src={article?.node?.image?.url}
                    alt="Banner"
                    className="w-full object-cover duration-1000 ease-in-out hover:scale-[1.07]"
                    height={535}
                    width={990}
                  />
                </div>
              </div>
            )}

            <div className="hidden items-center bg-white p-6 sm:flex">
              <span className="mr-4 text-base text-gray-700"> Share </span>
              <ul className="flex items-center gap-3">
                <li>
                  <Link
                    href="https://www.facebook.com/sharer.php?u=https://reebelo.com.au/blogs/reebelo-australia-featured-on-ausbiz"
                    passHref
                  >
                    <a className="inline-block align-middle opacity-40 hover:opacity-100">
                      <Icons.Facebook />
                    </a>
                  </Link>
                </li>
                <li>
                  <Link
                    href="https://www.pinterest.com/pin/create/button/?url=https://reebelo.com.au/blogs/reebelo-australia-featured-on-ausbiz&media=https://cdn.shopify.com/s/files/1/0423/2750/7093/articles/Thumbnail_large.jpg?v=1603871951&description=Managing%20Director,%20Gulrez%20Tyebji,%20was%20live%20on%20the%20Startup%20Daily%20show%20on%20ausbiz%20to%20share..."
                    passHref
                  >
                    <a className="inline-block align-middle opacity-40 hover:opacity-100">
                      <Icons.Pinterest />
                    </a>
                  </Link>
                </li>
                <li>
                  <Link
                    href="https://twitter.com/intent/tweet?text=Reebelo%20Australia%20featured%20on%20AusBiz&url=https%3A%2F%2Freebelo.com.au%2Fblogs%2Fnews%2Freebelo-australia-featured-on-ausbiz"
                    passHref
                  >
                    <a className="inline-block align-middle opacity-40 hover:opacity-100">
                      <Icons.Twitter />
                    </a>
                  </Link>
                </li>
                <li>
                  <Link
                    href="mailto:?&subject=Reebelo Australia featured on AusBiz&body=https://reebelo.com.au/blogs/reebelo-australia-featured-on-ausbiz"
                    passHref
                  >
                    <a className="inline-block align-middle opacity-40 hover:opacity-100">
                      <Icons.Email />
                    </a>
                  </Link>
                </li>
              </ul>
            </div>
            <div className="mt-10 block md:mt-14">
              <div className={`${styles.content}`}>
                {article && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: contentHtml || '',
                    }}
                  ></div>
                )}
              </div>

              <div className="mt-10 mb-8 flex items-center">
                {article && article.node.tags.length > 0 && (
                  <>
                    <span className="mr-4 text-sm text-gray-700 sm:text-base">
                      Tags:
                    </span>
                    <ul className="flex flex-wrap items-center gap-1">
                      {article?.node.tags.map((tag) => (
                        <li key={tag}>
                          <Link
                            href={`/blogs?tagged=${tag.toLowerCase()}`}
                            passHref
                          >
                            <a className="inline-block text-sm capitalize text-blue-500 hover:underline sm:text-base">
                              {tag}
                            </a>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>

              <div className="flex items-center">
                <span className="mr-4 text-sm text-gray-700 sm:text-base">
                  Share
                </span>
                <ul className="flex items-center gap-3">
                  <li>
                    <Link
                      href="https://www.facebook.com/sharer.php?u=https://reebelo.com.au/blogs/reebelo-australia-featured-on-ausbiz"
                      passHref
                    >
                      <a className="inline-block align-middle opacity-40 hover:opacity-100">
                        <Icons.Facebook />
                      </a>
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="https://www.pinterest.com/pin/create/button/?url=https://reebelo.com.au/blogs/reebelo-australia-featured-on-ausbiz&media=https://cdn.shopify.com/s/files/1/0423/2750/7093/articles/Thumbnail_large.jpg?v=1603871951&description=Managing%20Director,%20Gulrez%20Tyebji,%20was%20live%20on%20the%20Startup%20Daily%20show%20on%20ausbiz%20to%20share..."
                      passHref
                    >
                      <a className="inline-block align-middle opacity-40 hover:opacity-100">
                        <Icons.Pinterest />
                      </a>
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="https://twitter.com/intent/tweet?text=Reebelo%20Australia%20featured%20on%20AusBiz&url=https%3A%2F%2Freebelo.com.au%2Fblogs%2Fnews%2Freebelo-australia-featured-on-ausbiz"
                      passHref
                    >
                      <a className="inline-block align-middle opacity-40 hover:opacity-100">
                        <Icons.Twitter />
                      </a>
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="mailto:?&subject=Reebelo Australia featured on AusBiz&body=https://reebelo.com.au/blogs/reebelo-australia-featured-on-ausbiz"
                      passHref
                    >
                      <a className="inline-block align-middle opacity-40 hover:opacity-100">
                        <Icons.Email />
                      </a>
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="mt-12 mb-10 flex items-center justify-between border-b border-t border-gray-700/10 py-10 md:mt-14">
                <div>
                  {prevArticle && (
                    <Link href={`/blogs/${prevArticle?.node.handle}`} passHref>
                      <a className="flex items-center text-sm font-bold text-gray-700 hover:text-blue-500">
                        <svg
                          width="8"
                          height="12"
                          viewBox="0 0 8 12"
                          role="img"
                        >
                          <path
                            stroke="currentColor"
                            stroke-width="2"
                            d="M6 10L2 6l4-4"
                            fill="none"
                            stroke-linecap="square"
                          ></path>
                        </svg>
                        <span className="pl-3"> Previous article </span>
                      </a>
                    </Link>
                  )}
                </div>
                <div>
                  {nextArticle && (
                    <Link href={`/blogs/${nextArticle?.node.handle}`} passHref>
                      <a className="flex items-center text-sm font-bold text-gray-700 hover:text-blue-500">
                        <span className="pr-3"> Next article </span>
                        <svg
                          width="8"
                          height="12"
                          viewBox="0 0 8 12"
                          role="img"
                        >
                          <path
                            stroke="currentColor"
                            stroke-width="2"
                            d="M2 2l4 4-4 4"
                            fill="none"
                            stroke-linecap="square"
                          ></path>
                        </svg>
                      </a>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="hidden w-full lg:block lg:w-[40%] xl:w-[30%]">
            <Sidebar articles={props.blog.articles} />
          </div>
        </div>
      </div>
    </div>
  );
}
